import cn from 'classnames';
import { ReactElement, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Placement } from '../designConstants';

interface ClampedTextProps {
  fullText: string;
  maxLines?: number;
  className?: string;
  children: ReactNode;
}

export const ClampedText = ({ fullText, maxLines = 4, className, children }: ClampedTextProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const measure = () => {
      if (!el.clientWidth) {
        return;
      }
      // Use a DOM probe instead of canvas measurement so that web fonts,
      // hyphens, and overflow-wrap are handled by the browser's own layout engine.
      const computed = getComputedStyle(el);
      const lineHeight = parseFloat(computed.lineHeight) || 14;
      const probe = document.createElement('div');
      probe.style.cssText = `
        position: fixed;
        visibility: hidden;
        top: -9999px;
        left: -9999px;
        width: ${el.clientWidth}px;
        font-size: ${computed.fontSize};
        font-family: ${computed.fontFamily};
        font-weight: ${computed.fontWeight};
        font-style: ${computed.fontStyle};
        line-height: ${computed.lineHeight};
        overflow-wrap: break-word;
        hyphens: auto;
        white-space: normal;
      `;
      probe.textContent = fullText;
      document.body.appendChild(probe);
      const fullHeight = probe.offsetHeight;
      document.body.removeChild(probe);
      setIsClamped(fullHeight > lineHeight * maxLines + 1);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fullText, maxLines]);

  const tooltipId = `clamped-${fullText
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')}`;

  return (
    <ClampedWrapper active={isClamped} tooltipId={tooltipId} fullText={fullText}>
      <div ref={ref} className={cn(className)}>
        {children}
      </div>
    </ClampedWrapper>
  );
};

interface ClampedWrapperProps {
  active: boolean;
  tooltipId: string;
  fullText: string;
  children: ReactElement;
}

const ClampedWrapper = ({ active, tooltipId, fullText, children }: ClampedWrapperProps) => {
  if (!active) {
    return children;
  }

  return (
    <OverlayTrigger
      placement={Placement.TOP}
      overlay={<Tooltip id={tooltipId}>{fullText}</Tooltip>}
    >
      {children}
    </OverlayTrigger>
  );
};
